import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ArrowDown,
  BotMessageSquare,
  Sparkles,
  Check,
  Copy,
  CornerDownRight,
  Expand,
  History,
  Plus,
  Trash2,
  Maximize2,
  Minimize2,
  Send,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";
import {
  aiApi,
  aiErrorAlreadyShown,
  aiErrorMessage,
  type AiStatus,
} from "../../api/aiApi";
import { useAiAssistantStore } from "../../store/aiAssistant.store";
import {
  AiGlyph,
  AiText,
  AiNotice,
  AiWordmark,
  ConfidenceBadge,
  SourceList,
} from "./AiVisuals";
import { openAiSource } from "./aiEvents";
import { AiModelPicker } from "./AiModelPicker";
import { useAiModel } from "./useAiModel";
import {
  AssistantWelcome,
  PendingQuestion,
  QuickActions,
  RecordTips,
  TryAsking,
} from "./AssistantGuide";
import { uiLocale } from "../../utils/date/uiLocale";

const CONTRACT_PROMPTS = [
  { key: "commercial", text: "What are the key commercial terms?" },
  { key: "termination", text: "Summarize the termination provisions." },
  { key: "vendorObligations", text: "What obligations does the vendor have?" },
  { key: "unusual", text: "Are there any unusual clauses?" },
  { key: "liability", text: "Is there a liability cap?" },
];

const OBLIGATION_PROMPTS = [
  {
    key: "whatRequired",
    text: "What is required to complete this obligation?",
  },
  { key: "whenDue", text: "When is this obligation due?" },
  { key: "evidenceNeeded", text: "What evidence is required?" },
  { key: "blocking", text: "What is blocking this obligation?" },
  { key: "missed", text: "What happens if this obligation is missed?" },
];

const RECEIVABLE_PROMPTS = [
  { key: "howMuchOwed", text: "How much is still owed, and on which invoices?" },
  { key: "whatSent", text: "What has been sent to the debtor so far?" },
  { key: "paymentsReceived", text: "Which payments have been received?" },
  { key: "nextRecoveryStep", text: "What is the next recovery step, and when?" },
  { key: "latePaymentTerms", text: "Does the contract allow interest or late fees?" },
];

/**
 * What to ask next. Offered after an answer so the conversation can carry on
 * without composing a question from scratch; anything already asked in this
 * conversation is dropped rather than suggested twice.
 */
const CONTRACT_FOLLOW_UPS = [
  { key: "risks", text: "What are the main risks for us here?" },
  { key: "renewal", text: "How does renewal or extension work?" },
  { key: "payment", text: "What are the payment terms and penalties?" },
  { key: "confidentiality", text: "What does it say about confidentiality?" },
  { key: "exitRights", text: "How can we exit this agreement early?" },
  { key: "governingLaw", text: "Which law and courts govern it?" },
];

const OBLIGATION_FOLLOW_UPS = [
  { key: "whoResponsible", text: "Who is responsible for this obligation?" },
  { key: "clauseText", text: "Show me the clause this comes from." },
  { key: "proof", text: "What would count as acceptable proof?" },
  { key: "consequence", text: "What is the consequence of missing it?" },
  { key: "related", text: "Which other obligations depend on this one?" },
];

const RECEIVABLE_FOLLOW_UPS = [
  { key: "whyOverdue", text: "Which invoice is the most overdue?" },
  { key: "escalate", text: "Has a formal or legal notice been sent yet?" },
  { key: "pauseReason", text: "Has recovery been paused or disputed, and why?" },
  { key: "disputeClause", text: "What does the contract say about disputed invoices?" },
  { key: "recoveredShare", text: "How much has been recovered so far?" },
];

/** Top-bar entry point, beside notifications. Rendered only when enableEmeisAI is on. */
export function AiAssistantButton() {
  const { t } = useTranslation("ai");
  const toggle = useAiAssistantStore((s) => s.toggle);
  const open = useAiAssistantStore((s) => s.open);
  const label = t("assistant.open", { defaultValue: "Ask lawyal.ai" });

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={open}
      aria-label={label}
      // It shows its own name card on hover, so the app-wide tooltip stays off.
      data-tooltip-host
      className={clsx(
        // A floating action button at the corner, where a chat launcher is
        // looked for: a gradient tile with the AI spark. It breathes gently
        // (see .ai-launcher) so it is noticed, names itself on hover or
        // focus, and steps aside while the panel is open, which sits in the
        // same place.
        "ai-launcher group fixed bottom-10 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[rgb(var(--accent-fill))] to-[rgb(var(--accent-fill))]/80 text-[rgb(var(--accent-ink))] shadow-lg shadow-[rgb(var(--text-accent))]/30 ring-1 ring-[rgb(var(--accent-ink))]/20 transition-[box-shadow,transform,opacity] duration-200 hover:shadow-xl hover:shadow-[rgb(var(--text-accent))]/40 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgb(var(--text-accent))]/35",
        open && "pointer-events-none translate-y-2 opacity-0",
      )}
    >
      <span
        aria-hidden
        className="ai-launcher-ring pointer-events-none absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-[rgb(var(--accent-fill))] to-[rgb(var(--accent-fill))]/80 opacity-40"
      />
      {/* A soft sheen across the top edge gives the tile some depth. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-1 top-1 h-1/2 rounded-xl bg-gradient-to-b from-white/25 to-transparent"
      />
      <Sparkles
        size={24}
        strokeWidth={2.2}
        aria-hidden
        className="relative shrink-0 drop-shadow-sm transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110"
      />
      {/* The product's name slides out beside the button on hover or focus. */}
      <span
        aria-hidden
        className="pointer-events-none absolute right-full mr-3 flex translate-x-1 items-center whitespace-nowrap rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 py-1.5 opacity-0 shadow-md transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100"
      >
        <AiWordmark height={18} className="text-[rgb(var(--text-accent))]" />
      </span>
    </button>
  );
}

/** The answer's text, with a copy button that confirms in place. */
function CopyAnswer({ text }: { text: string }) {
  const { t } = useTranslation("ai");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={() => {
        // Clipboard access can be refused (an insecure origin, a denied
        // permission); the answer is still on screen either way.
        navigator.clipboard
          ?.writeText(text)
          .then(() => setCopied(true))
          .catch(() => setCopied(false));
      }}
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-[rgb(var(--text-muted))] transition-colors hover:bg-[rgb(var(--card))] hover:text-[rgb(var(--text-accent))]"
    >
      {copied ? (
        <Check size={12} aria-hidden />
      ) : (
        <Copy size={12} aria-hidden />
      )}
      {copied
        ? t("actions.copied", { defaultValue: "Copied" })
        : t("actions.copy", { defaultValue: "Copy" })}
    </button>
  );
}

/** Three dots, while the model reads the record. */
function Thinking({ label }: { label: string }) {
  return (
    <div
      role="status"
      className="inline-flex items-center gap-2 rounded-2xl rounded-bl-sm border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 py-2.5 text-xs text-[rgb(var(--text-secondary))] shadow-lifted"
    >
      <span className="flex items-center gap-1" aria-hidden>
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            style={{ animationDelay: `${delay}ms` }}
            className="h-1.5 w-1.5 dot-breathe rounded-full bg-[rgb(var(--accent-fill))]"
          />
        ))}
      </span>
      {label}
    </div>
  );
}

/**
 * The context-aware assistant. It only ever asks about the record the user
 * has open (set by that page), and the server checks the user's access to it
 * again on every question.
 */
export function AiAssistantPanel() {
  const { t } = useTranslation("ai");
  const {
    open,
    context,
    threads,
    activeThreadByContext,
    close,
    ensureThread,
    startThread,
    selectThread,
    deleteThread,
    addTurn,
    updateTurn,
    pendingQuestion,
    setPendingQuestion,
  } = useAiAssistantStore();
  const [question, setQuestion] = useState("");
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  /**
   * How much room the panel takes: compact beside the record, wide for a long
   * answer, full when the conversation is the work.
   */
  const [size, setSize] = useState<"compact" | "wide" | "full">("compact");
  const [atBottom, setAtBottom] = useState(true);
  /** The saved-conversations list, the way a chat app keeps its past chats. */
  const [historyOpen, setHistoryOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // The conversation on screen: this record's current thread, created on the
  // first question rather than the moment the panel opens.
  const activeThreadId = context
    ? activeThreadByContext[context.id]
    : undefined;
  const activeThread = activeThreadId ? threads[activeThreadId] : undefined;
  const conversation = activeThread?.turns ?? [];
  /**
   * This record's saved conversations, newest first. A contract's history
   * belongs to that contract: another record's chats are never listed here.
   */
  const history = Object.values(threads)
    .filter(
      (thread) =>
        thread.turns.length > 0 && thread.contextId === (context?.id ?? ""),
    )
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const isObligation = context?.kind === "obligation";
  const isReceivable = context?.kind === "receivable";
  const prompts = isReceivable ? RECEIVABLE_PROMPTS : isObligation ? OBLIGATION_PROMPTS : CONTRACT_PROMPTS;
  const asked = new Set(
    conversation.map((turn) => turn.question.trim().toLowerCase()),
  );
  const followUps = (isReceivable ? RECEIVABLE_FOLLOW_UPS : isObligation ? OBLIGATION_FOLLOW_UPS : CONTRACT_FOLLOW_UPS)
    .map((p) => t(`followUps.${p.key}`, { defaultValue: p.text }))
    .filter((label) => !asked.has(label.trim().toLowerCase()))
    .slice(0, 3);
  const busy = conversation.some((turn) => turn.status === "pending");
  const [model] = useAiModel("assistant");
  const lastStatus = conversation[conversation.length - 1]?.status;

  useEffect(() => {
    if (!open || status) return;
    aiApi
      .status()
      .then(setStatus)
      .catch((error) =>
        setStatusError(
          aiErrorMessage(error, "AI service is temporarily unavailable."),
        ),
      );
  }, [open, status]);

  useEffect(() => {
    listRef.current?.scrollTo?.({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [conversation.length, lastStatus]);

  // Opening a record picks up the conversation last held about it, rather
  // than starting from the empty state every visit.
  useEffect(() => {
    if (!open || !context) return;
    ensureThread(context);
  }, [open, context, ensureThread]);

  // Escape closes the panel, like every other overlay in the app.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  // The composer grows with the question instead of scrolling inside itself.
  useLayoutEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, 160)}px`;
  }, [question]);

  if (!open) return null;

  const ask = async (text: string) => {
    const trimmed = text.trim();
    if (!context || !trimmed || busy) return;
    const threadId = ensureThread(context);
    const id = uuidv4();
    addTurn(threadId, { id, question: trimmed, status: "pending" });
    setQuestion("");
    setHistoryOpen(false);
    try {
      const answer = isReceivable
        ? await aiApi.askReceivable(context.id, trimmed, model)
        : isObligation
          ? await aiApi.askObligation(context.id, trimmed, model)
          : await aiApi.ask(context.id, trimmed, model);
      updateTurn(threadId, id, { status: "answered", answer });
    } catch (error) {
      updateTurn(threadId, id, {
        status: "error",
        error: aiErrorAlreadyShown(error)
          ? t("assistant.notAllowed", {
              defaultValue:
                "This question could not be answered with your current access or plan.",
            })
          : aiErrorMessage(
              error,
              t("assistant.failed", {
                defaultValue: "The question could not be answered.",
              }),
            ),
      });
    }
  };

  const unavailable =
    statusError ?? (status && !status.enabled ? status.message : null);
  const title = !context
    ? t("assistant.title", { defaultValue: "Ask lawyal.ai" })
    : isReceivable
      ? t("assistant.receivableTitle", {
          defaultValue: "Ask AI about this receivable",
        })
      : isObligation
      ? t("assistant.obligationTitle", {
          defaultValue: "Ask AI about this obligation",
        })
      : t("assistant.contractTitle", {
          defaultValue: "Ask AI about this contract",
        });

  const headerButton =
    "rounded-lg p-1.5 text-[rgb(var(--accent-ink))]/80 transition-colors hover:bg-[rgb(var(--accent-ink))]/10 hover:text-[rgb(var(--accent-ink))]";

  return (
    <aside
      aria-label={t("assistant.title", { defaultValue: "Ask lawyal.ai" })}
      className={clsx(
        "fixed z-50 flex flex-col overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] shadow-2xl shadow-slate-900/20 transition-all duration-200",
        size === "full"
          ? "inset-3 sm:inset-6"
          : "bottom-4 right-4 top-16 w-[92vw]",
        size === "wide" && "sm:w-[680px]",
        size === "compact" && "sm:w-[440px]",
      )}
    >
      {/* Header: the mark, what the questions are about, and the panel's own controls. */}
      <header className="flex items-start justify-between gap-2 bg-gradient-to-r from-[rgb(var(--accent-fill))] to-[rgb(var(--accent-fill))]/85 px-4 py-3 text-[rgb(var(--accent-ink))]">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="flex shrink-0 items-center gap-2 rounded-xl bg-[rgb(var(--accent-ink))]/10 px-2.5 py-2 ring-1 ring-inset ring-[rgb(var(--accent-ink))]/20">
            <AiWordmark height={20} />
            <BotMessageSquare size={16} aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold">{title}</p>
            {context && (
              <p className="truncate text-xs text-[rgb(var(--accent-ink))]/80">{context.label}</p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-pressed={historyOpen}
            title={t("assistant.history", {
              defaultValue: "Saved conversations",
            })}
            aria-label={t("assistant.history", {
              defaultValue: "Saved conversations",
            })}
            className={clsx(
              headerButton,
              historyOpen && "bg-[rgb(var(--accent-ink))]/15 text-[rgb(var(--accent-ink))]",
            )}
          >
            <History size={15} />
          </button>
          {context && conversation.length > 0 && (
            <button
              type="button"
              onClick={() => {
                startThread(context);
                setHistoryOpen(false);
              }}
              title={t("assistant.newChat", { defaultValue: "New chat" })}
              aria-label={t("assistant.newChat", { defaultValue: "New chat" })}
              className={headerButton}
            >
              <Plus size={16} />
            </button>
          )}
          {size !== "compact" && (
            <button
              type="button"
              onClick={() => setSize("compact")}
              title={t("assistant.narrow", {
                defaultValue: "Narrow the panel",
              })}
              aria-label={t("assistant.narrow", {
                defaultValue: "Narrow the panel",
              })}
              className={clsx(headerButton, "hidden sm:inline-flex")}
            >
              <Minimize2 size={15} />
            </button>
          )}
          {size !== "wide" && (
            <button
              type="button"
              onClick={() => setSize("wide")}
              title={t("assistant.widen", { defaultValue: "Widen the panel" })}
              aria-label={t("assistant.widen", {
                defaultValue: "Widen the panel",
              })}
              className={clsx(headerButton, "hidden sm:inline-flex")}
            >
              <Maximize2 size={15} />
            </button>
          )}
          {size !== "full" && (
            <button
              type="button"
              onClick={() => setSize("full")}
              title={t("assistant.full", {
                defaultValue: "Fill the window",
              })}
              aria-label={t("assistant.full", {
                defaultValue: "Fill the window",
              })}
              className={clsx(headerButton, "hidden sm:inline-flex")}
            >
              <Expand size={15} />
            </button>
          )}
          <button
            type="button"
            onClick={close}
            aria-label={t("actions.close", { defaultValue: "Close" })}
            className={headerButton}
          >
            <X size={16} />
          </button>
        </div>
      </header>

      {/* Saved conversations: every thread, newest first, with the record it
          was held about. Selecting one opens it; the bin deletes it for good. */}
      {historyOpen && (
        <div className="max-h-64 overflow-y-auto border-b border-[rgb(var(--border))] bg-[rgb(var(--card-muted))]/50 px-3 py-2">
          <div className="mb-1.5 flex items-center justify-between px-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[rgb(var(--text-muted))]">
              {t("assistant.history", { defaultValue: "Saved conversations" })}
            </p>
            {context && (
              <button
                type="button"
                onClick={() => {
                  startThread(context);
                  setHistoryOpen(false);
                }}
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-semibold text-[rgb(var(--text-accent))] hover:bg-[rgb(var(--card))]"
              >
                <Plus size={12} />
                {t("assistant.newChat", { defaultValue: "New chat" })}
              </button>
            )}
          </div>

          {history.length === 0 ? (
            <p className="px-1 py-2 text-xs text-[rgb(var(--text-muted))]">
              {t("assistant.historyEmpty", {
                defaultValue:
                  "Conversations about this record are kept here until you delete them.",
              })}
            </p>
          ) : (
            <ul className="space-y-1">
              {history.map((thread) => {
                const isActive = thread.id === activeThreadId;
                const title = thread.turns[0]?.question ?? "";
                return (
                  <li key={thread.id}>
                    <div
                      className={clsx(
                        "group flex items-center gap-2 rounded-lg border px-2.5 py-2 transition-colors",
                        isActive
                          ? "border-[rgb(var(--text-accent))]/45 bg-[rgb(var(--text-accent))]/[0.08]"
                          : "border-transparent bg-[rgb(var(--card))] hover:border-[rgb(var(--border))]",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          selectThread(thread.id);
                          setHistoryOpen(false);
                        }}
                        className="min-w-0 flex-1 text-left"
                      >
                        <span className="block truncate text-xs font-medium text-[rgb(var(--text-primary))]">
                          {title}
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-[rgb(var(--text-muted))]">
                          {new Date(thread.updatedAt).toLocaleString(uiLocale())} ·{" "}
                          {t("assistant.historyCount", {
                            defaultValue: "{{count}} question(s)",
                            count: thread.turns.length,
                          })}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteThread(thread.id)}
                        title={t("assistant.deleteChat", {
                          defaultValue: "Delete this conversation",
                        })}
                        aria-label={t("assistant.deleteChat", {
                          defaultValue: "Delete this conversation",
                        })}
                        className="shrink-0 rounded-md p-1.5 text-[rgb(var(--text-muted))] opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100 dark:hover:bg-red-500/10"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <div className="relative flex-1 overflow-hidden">
        <div
          ref={listRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
          }}
          className="h-full space-y-4 overflow-y-auto bg-[rgb(var(--card-muted))]/30 px-4 py-4"
        >
          {!context && <AssistantWelcome onPickExample={setPendingQuestion} />}

          {context && unavailable && (
            <p
              role="status"
              className="rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
            >
              {unavailable}
            </p>
          )}

          {context && !unavailable && conversation.length === 0 && (
            <div className="space-y-4">
              {pendingQuestion ? (
                <PendingQuestion
                  question={pendingQuestion}
                  recordLabel={context.label}
                  onAsk={() => {
                    const q = pendingQuestion;
                    setPendingQuestion(null);
                    void ask(q);
                  }}
                  onDismiss={() => setPendingQuestion(null)}
                />
              ) : (
                <div className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3.5 py-3">
                  <p className="text-sm font-semibold text-[rgb(var(--text-primary))]">
                    {t("assistant.emptyTitle", {
                      defaultValue: "Ask about this record",
                    })}
                  </p>
                  <p className="mt-0.5 text-xs text-[rgb(var(--text-secondary))]">
                    {t("assistant.emptyHint", {
                      defaultValue:
                        "Answers are drawn from this record and cite where they came from.",
                    })}
                  </p>
                </div>
              )}
              <QuickActions
                kind={context.kind}
                onAsk={(q) => void ask(q)}
                disabled={busy}
              />
              <TryAsking
                prompts={prompts.map((p) =>
                  t(`prompts.${p.key}`, { defaultValue: p.text }),
                )}
                onAsk={(q) => void ask(q)}
              />
              <RecordTips kind={context.kind} />
            </div>
          )}

          {/* A question picked earlier, offered alongside an existing conversation too. */}
          {context && !unavailable && conversation.length > 0 && pendingQuestion && (
            <PendingQuestion
              question={pendingQuestion}
              recordLabel={context.label}
              onAsk={() => {
                const q = pendingQuestion;
                setPendingQuestion(null);
                void ask(q);
              }}
              onDismiss={() => setPendingQuestion(null)}
            />
          )}

          {conversation.map((turn) => (
            <div key={turn.id} className="space-y-2">
              <p className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-[rgb(var(--accent-fill))] px-3.5 py-2 text-sm text-[rgb(var(--accent-ink))] shadow-sm">
                {turn.question}
              </p>

              <div className="flex items-start gap-2">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--text-accent))]/10 text-[rgb(var(--text-accent))] ring-1 ring-inset ring-[rgb(var(--text-accent))]/15">
                  <AiGlyph size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  {turn.status === "pending" && (
                    <Thinking
                      label={
                        isReceivable
                          ? t("assistant.thinkingReceivable", {
                              defaultValue: "Reading the receivable and its invoices…",
                            })
                          : isObligation
                          ? t("assistant.thinkingObligation", {
                              defaultValue:
                                "Reading the obligation and its clause…",
                            })
                          : t("assistant.thinking", {
                              defaultValue: "Reading the contract…",
                            })
                      }
                    />
                  )}
                  {turn.status === "error" && (
                    <p
                      role="alert"
                      className="rounded-2xl rounded-bl-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"
                    >
                      {turn.error}
                    </p>
                  )}
                  {turn.status === "answered" && turn.answer?.result && (
                    <div className="rounded-2xl rounded-bl-sm border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3.5 py-3 shadow-lifted">
                      <div className="mb-1.5 flex flex-wrap items-center gap-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-[rgb(var(--text-accent))]">
                          {t("assistant.answer", { defaultValue: "AI Answer" })}
                        </span>
                        {turn.answer.result.answerable && (
                          <ConfidenceBadge
                            confidence={turn.answer.result.confidence}
                          />
                        )}
                        <span className="ml-auto">
                          <CopyAnswer text={turn.answer.result.text} />
                        </span>
                      </div>
                      <AiText text={turn.answer.result.text} />
                      <SourceList
                        statement={turn.answer.result}
                        // Only a contract page can scroll its document to a cited passage.
                        onOpenSource={
                          context && context.kind === "contract"
                            ? (source) =>
                                openAiSource({ contractId: context.id, source })
                            : undefined
                        }
                      />
                      <div className="mt-2">
                        <AiNotice
                          notice={turn.answer.notice}
                          warnings={turn.answer.warnings}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* What to ask next, once there is an answer to build on. */}
          {context &&
            !unavailable &&
            !busy &&
            conversation.length > 0 &&
            followUps.length > 0 && (
              <div className="space-y-1.5 pl-9">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[rgb(var(--text-muted))]">
                  {t("assistant.followUps", { defaultValue: "Ask next" })}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {followUps.map((label) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => void ask(label)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-2.5 py-1 text-xs text-[rgb(var(--text-primary))] transition-colors hover:border-[rgb(var(--text-accent))]/50 hover:bg-[rgb(var(--text-accent))]/[0.06] hover:text-[rgb(var(--text-accent))]"
                    >
                      <CornerDownRight
                        size={12}
                        aria-hidden
                        className="text-[rgb(var(--text-muted))]"
                      />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
        </div>

        {/* Only while there is something above the fold to come back from. */}
        {!atBottom && conversation.length > 0 && (
          <button
            type="button"
            onClick={() =>
              listRef.current?.scrollTo({
                top: listRef.current.scrollHeight,
                behavior: "smooth",
              })
            }
            aria-label={t("assistant.scrollDown", {
              defaultValue: "Jump to the latest answer",
            })}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-2 text-[rgb(var(--text-accent))] shadow-md transition-colors hover:bg-[rgb(var(--card-muted))]"
          >
            <ArrowDown size={14} />
          </button>
        )}
      </div>

      {context && (
        <form
          className="border-t border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 py-3"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(question);
          }}
        >
          <div className="flex items-end gap-2 rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 py-2 transition-colors focus-within:border-[rgb(var(--text-accent))] shadow-lifted">
            <textarea
              ref={inputRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void ask(question);
                }
              }}
              maxLength={1000}
              rows={1}
              disabled={Boolean(unavailable)}
              placeholder={
                isReceivable
                  ? t("assistant.placeholderReceivable", {
                      defaultValue: "Ask a question about this receivable…",
                    })
                  : isObligation
                  ? t("assistant.placeholderObligation", {
                      defaultValue: "Ask a question about this obligation…",
                    })
                  : t("assistant.placeholder", {
                      defaultValue: "Ask a question about this contract…",
                    })
              }
              className="max-h-40 min-h-[24px] flex-1 resize-none bg-transparent text-sm text-[rgb(var(--text-primary))] outline-none placeholder:text-[rgb(var(--text-muted))]"
            />
            <button
              type="submit"
              disabled={busy || !question.trim() || Boolean(unavailable)}
              aria-label={t("assistant.send", { defaultValue: "Ask" })}
              className="shrink-0 rounded-xl bg-[rgb(var(--accent-fill))] p-2 text-[rgb(var(--accent-ink))] transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              <Send size={15} />
            </button>
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-2 px-1 text-[11px] text-[rgb(var(--text-muted))]">
            <AiModelPicker
              surface="assistant"
              disabled={busy || Boolean(unavailable)}
              className="!w-36"
              triggerClassName="h-7 py-0 text-[11px]"
            />
            <span className="flex-1">
              {t("assistant.enterHint", {
                defaultValue: "Enter to send · Shift + Enter for a new line",
              })}
            </span>
            {question.length > 0 && <span>{question.length}/1000</span>}
          </div>
        </form>
      )}
    </aside>
  );
}
